// Geometry helpers for Phase-1 cabinet meshes.
//
// All dimensions in inches. Cabinet-local frame:
//   X: right
//   Y: up
//   Z: out from back wall (Z=0 = back face, Z=+D = front face)
//   Origin: bottom-front-left exterior corner is at (0, 0, D).
//   Actually we standardise on origin = bottom-back-left exterior corner:
//     left side panel spans x∈[0,T], y∈[0,H], z∈[0,D]
//     right side panel spans x∈[W-T,W], y∈[0,H], z∈[0,D]
//   This keeps placement intuitive: `position` is the back-left foot of the
//   cabinet, and rotation pivots around that point.

import type { CabinetMeshChunk } from "@/lib/types";

/** Build a box mesh chunk axis-aligned to the cabinet-local frame.
 *  `min` and `max` are opposite corners in cabinet-local inches. */
export function boxChunk(
  name: string,
  min: [number, number, number],
  max: [number, number, number],
  color: string,
): CabinetMeshChunk {
  const [x0, y0, z0] = min;
  const [x1, y1, z1] = max;

  const vertices = [
    x0, y0, z0,  x1, y0, z0,  x1, y1, z0,  x0, y1, z0,    // back face   (z = z0)
    x0, y0, z1,  x1, y0, z1,  x1, y1, z1,  x0, y1, z1,    // front face  (z = z1)
  ];

  // CCW winding from outside the box.
  const indices = [
    // back (looking from -z toward +z)
    0, 2, 1,   0, 3, 2,
    // front (from +z toward -z)
    4, 5, 6,   4, 6, 7,
    // left (x = x0)
    0, 4, 7,   0, 7, 3,
    // right (x = x1)
    1, 2, 6,   1, 6, 5,
    // bottom (y = y0)
    0, 1, 5,   0, 5, 4,
    // top (y = y1)
    3, 7, 6,   3, 6, 2,
  ];

  return { name, vertices, indices, color };
}

// ── Color palette (Phase 1 placeholders) ─────────────────────────

export const COLORS = {
  carcass:    "#dec3a4",  // birch ply
  back:       "#9a7a55",  // hardboard
  door:       "#5e3b1f",  // walnut accent
  drawerFront:"#5e3b1f",
  shelf:      "#dec3a4",
  selected:   "#ffaa55",  // selection highlight overlay
} as const;

// ── Constants ────────────────────────────────────────────────────

/** Standard panel thickness (3/4" plywood / LDPS). */
export const T = 0.75;
/** Standard back thickness (1/4" hardboard). */
export const BACK_T = 0.25;
/** Door reveal (gap between cabinet exterior and door edge). */
export const REVEAL = 1 / 16;
/** Vertical gap between stacked door pairs. */
export const VGAP = 1 / 8;
